import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import { TreatmentReasonDto, TreatmentReasonSearchRequest, UpsertTreatmentReasonDto } from "ts-types/api.types";
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { FormState } from "final-form";
import VirtualizedTable from "components/layout/VirtualizedTable";
import UpsertTreatmentReasonForm from 'components/settings/treatment-reasons/UpsertTreatmentReasonsForm';
import {
  deleteTreatmentReason,
  findTreatmentReasonById,
  searchTreatmentReasons
} from 'services/treatmentReasonServices';
import { noop } from 'utils/functionUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

const initialTreatmentReasonSearch: Partial<TreatmentReasonSearchRequest> = {};

interface TreatmentReasonFieldProps extends FormState {
  values: Partial<TreatmentReasonSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundTreatmentReasons: Array<TreatmentReasonDto>,
  treatmentReasonsLoaded: boolean,
  treatmentReason?: UpsertTreatmentReasonDto,
  treatmentReasonId?: number,
  addEditModalOpen: boolean,
  loading: boolean,
  searchValues: Partial<TreatmentReasonSearchRequest>,
  cancelTokenSource: CancelTokenSource;
}

class TreatmentReasonsView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundTreatmentReasons: [],
      treatmentReasonsLoaded: false,
      treatmentReason: undefined,
      treatmentReasonId: undefined,
      addEditModalOpen: false,
      loading: false,
      searchValues: initialTreatmentReasonSearch,
      cancelTokenSource
    }

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findTreatmentReasonById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, treatmentReason?: UpsertTreatmentReasonDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        treatmentReason: treatmentReason,
        treatmentReasonId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      treatmentReasonId: undefined,
      treatmentReason: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        treatmentReasonsLoaded: true
      })
    };

    searchTreatmentReasons(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundTreatmentReasons: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteTreatmentReason = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteTreatmentReason(id, cancelTokenSource).then(response => {
      this.setState(({foundTreatmentReasons}: State) => ({
        foundTreatmentReasons: foundTreatmentReasons.filter((treatmentReason: TreatmentReasonDto) => treatmentReason.id != response.id)
      }))
    }).catch(noop);
  };

  handleTreatmentReasonChange = debounce(({values}: TreatmentReasonFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({treatmentReasonsLoaded: false});

    const onFinally = () => {
      this.setState({
        treatmentReasonsLoaded: true
      })
    };

    searchTreatmentReasons(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundTreatmentReasons: response,
              searchValues: values
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {treatmentReasonsLoaded} = this.state;
    const treatmentReasonView = (
        <PraxStyledSegment>

          <div>
            {this.renderSearchTreatmentReasonsSegment()}
          </div>

          <StyledViewTableRow>
            {!treatmentReasonsLoaded ? this.renderLoader() : this.renderTreatmentReasonsSegment()}
          </StyledViewTableRow>

          {this.renderUpsertTreatmentReasonModal()}
        </PraxStyledSegment>
    );
    return (
        <React.Fragment>
          {treatmentReasonView}
        </React.Fragment>
    );
  }

  renderSearchTreatmentReasonsSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{}}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundTreatmentReasons} = this.state;

    const numResults = foundTreatmentReasons.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleTreatmentReasonChange}/>
        </PxGrid>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("treatmentReason.confirmDelete")}
              onConfirm={() => this.deleteTreatmentReason(cellData)}
          />
        </React.Fragment>
    );
  };

  treatmentReasonRowGetter = ({index}: any) => {
    const {foundTreatmentReasons} = this.state;

    Object.assign(foundTreatmentReasons[index], {index: index + 1});

    return foundTreatmentReasons[index];
  };

  foundTreatmentRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundTreatmentReasons} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundTreatmentReasons[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderTreatmentReasonsSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundTreatmentReasons} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundTreatmentReasons.length}
            rowGetter={this.treatmentReasonRowGetter}
            rowRenderer={this.foundTreatmentRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("treatmentReason.description")),
                dataKey: "description"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("treatmentReason.loader")} </Loader>
        </Dimmer>
    );
  };

  renderUpsertTreatmentReasonModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, treatmentReason, treatmentReasonId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("treatmentReasons")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertTreatmentReasonForm
                treatmentReason={treatmentReason}
                treatmentReasonId={treatmentReasonId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(TreatmentReasonsView);
